import React, { ReactElement, useState } from 'react';
import ReactDOM from 'react-dom';
import Slider, { Settings } from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { AdvancedImage, AdvancedVideo, lazyload } from '@cloudinary/react';
import { name } from '@cloudinary/url-gen/actions/namedTransformation';
import { byRadius } from '@cloudinary/url-gen/actions/roundCorners';
import { Cloudinary, CloudinaryImage, CloudinaryVideo } from '@cloudinary/url-gen';
import { thumbnail } from '@cloudinary/url-gen/actions/resize';
import { set } from '@cloudinary/url-gen/actions/variable';
import { CLOUDINARY_CLOUD_NAME, PUPPY_DEFAULT_IMAGE } from '../../../utils/constants';
import { setFallbackImage } from '../../../utils/imageHelper';
import FANextArrow from './components/fa-next-arrow';
import FAPrevArrow from './components/fa-prev-arrow';

interface SellerSliderImage {
  publicId: string;
  mediaType: string;
  fileType: string;
  overlayDate: string;
  parentLabel: string;
  alt: string;
}

interface SellerSliderProps {
  hasThumbnails: boolean;
  images: SellerSliderImage[];
  externalMedia: any;
}

const SellerSlider: React.FC<SellerSliderProps> = (data): ReactElement => {
  const {
    hasThumbnails,
    images,
    externalMedia,
  } = data;

  const cloudinary = new Cloudinary({
    cloud: {
      cloudName: CLOUDINARY_CLOUD_NAME,
    },
  });

  const mediaCount = images.length + externalMedia.length;
  const initialSlide = images.filter((item) => {
    return item.mediaType === 'video';
  }).length + externalMedia.length;

  const [slider1, setSlider1] = useState<Slider | undefined>(undefined);
  const [slider2, setSlider2] = useState<Slider | undefined>(undefined);

  const settings: Settings = {
    className: 'center',
    infinite: true,
    centerPadding: '60px',
    nextArrow: <FANextArrow />,
    prevArrow: <FAPrevArrow />,
    slidesToShow: 1,
    arrows: true,
    initialSlide,
  };

  const settingsTabs: Settings = {
    className: 'center',
    infinite: images.length > 4,
    slidesToShow: 4,
    arrows: false,
    focusOnSelect: true,
  };

  const cloudMedia = images.map((media) => {
    const sliderMedia = {
      publicId: media.publicId,
      mediaType: media.mediaType,
      thumb: CloudinaryImage || CloudinaryVideo,
      embedUrl: CloudinaryImage || CloudinaryVideo,
      alt: media.alt,
    };

    // TODO: videos need their own transformation
    if (media.mediaType === 'video') {
      // @ts-ignore
      sliderMedia.thumb = cloudinary.video(media.publicId)
        .resize(thumbnail()
          .width(150)
          .height(150))
        .roundCorners(byRadius(8))
        .format('jpg'); // Converts frame of video to an image

      // @ts-ignore
      sliderMedia.embedUrl = cloudinary.video(media.publicId)
        .roundCorners(byRadius(16));

      return sliderMedia;
    }

    // @ts-ignore
    sliderMedia.thumb = cloudinary
      .image(media.publicId)
      .resize(thumbnail()
        .width(150)
        .height(150))
      .roundCorners(byRadius(8));

    if (media.overlayDate) {
      // @ts-ignore
      sliderMedia.embedUrl = cloudinary.image(media.publicId)
        .addVariable(set('date', media.overlayDate))
        .namedTransformation(name('lpr-full-transform'))
        .namedTransformation(name('lpr-date-transform'))
        .roundCorners(byRadius(16));
    } else {
      // @ts-ignore
      sliderMedia.embedUrl = cloudinary.image(media.publicId)
        .namedTransformation(name('lpr-full-transform'))
        .roundCorners(byRadius(16));
    }

    return sliderMedia;
  });

  return (
    <>
      <Slider
        {...settings}
        asNavFor={slider1}
        ref={(slider: Slider) => (setSlider2(slider))}
      >
        {externalMedia.map((media) => (
          <div className="listing-main-slider" key={`cloud-medium-${media.id}`}>
            <div className="ar-box-square">
              <div className="ar-box-in">
                {/*  Youtube Embed Video  */}
                <iframe
                  title="Video Player"
                  src={media.embedUrl}
                />
              </div>
            </div>
          </div>
        ))}
        {/* MAIN SLIDER */}
        {cloudMedia.map((media) => (
          <div className="seller-main-slider px-4" key={`media-${media.publicId}`}>
            <div className="ar-box-square">
              <div className="ar-box-in">
                {media.mediaType === 'video' && (
                  // @ts-ignore
                  <AdvancedVideo cldVid={media.embedUrl} controls className="media-medium" title={media.alt} aria-label={media.alt} />)}
                {media.mediaType === 'image' && (
                  <AdvancedImage
                    plugins={[lazyload()]}
                    // @ts-ignore
                    cldImg={media.embedUrl}
                    className="media-medium"
                    onError={(e) => {
                      setFallbackImage(e, PUPPY_DEFAULT_IMAGE);
                    }}
                    alt={media.alt}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </Slider>

      {/* THUMB SLIDER */}
      {hasThumbnails && mediaCount > 1 && (
        <Slider
          {...settingsTabs}
          asNavFor={slider2}
          ref={(slider: Slider) => (setSlider1(slider))}
          className="slider-thumbnails"
        >
          {externalMedia.map((media) => (
            <div className="external-thumb-slider" key={`external-thumb-${media.id}`}>
              <div className="ar-box-square">
                <div className="ar-box-in">
                  <div className="video-thumb thumb" style={{ backgroundImage: `url('${media.thumb}')` }}>
                    <i className="fa fa-video" />
                  </div>
                </div>
              </div>
            </div>
          ))}
          {cloudMedia.map((media) => (
            <div key={`media-thumb-${media.publicId}`}>
              <div className="ar-box-square">
                <div className="ar-box-in">
                  <AdvancedImage
                    plugins={[lazyload()]}
                    // @ts-ignore
                    cldImg={media.thumb}
                    className="media-medium"
                    onError={(e) => {
                      setFallbackImage(e, PUPPY_DEFAULT_IMAGE);
                    }}
                    alt={media.alt}
                  />
                  {media.mediaType === 'video' ? (<i className="fa fa-video" />) : null}
                </div>
              </div>
            </div>
          ))}
        </Slider>
      )}
    </>
  );
};

if (document.getElementsByClassName('react-seller-slider').length > 0) {
  document.querySelectorAll('.react-seller-slider')
    .forEach((element) => {
      const hasThumbnails = element.getAttribute('data-has-thumbnails') === '1';
      const images = JSON.parse(element.getAttribute('data-images') ?? '');
      const externalMedia = JSON.parse(element.getAttribute('data-external-media') ?? '');
      ReactDOM.render(<SellerSlider
        images={images}
        externalMedia={externalMedia}
        hasThumbnails={hasThumbnails}
      />, element);
    });
}

export default SellerSlider;
